import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCellFeatures } from '../contexts/CellFeaturesContext';
import { peekTenantRuntime, isRouteEnabled } from '../config/tenant';
export const systemControl = 'rounded-ctl border border-line bg-panel px-3 py-2 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-signal focus-visible:outline-offset-2 disabled:opacity-50';
export function useSystemAccess(path: string) {
  const { user } = useAuth();
  const runtime = peekTenantRuntime();
  const enabled = isRouteEnabled(path, useCellFeatures(), runtime);
  const admin = runtime?.legacy ? user?.role === 'admin' : runtime?.role === 'owner';
  return { enabled, allowed: enabled && admin, legacy: runtime?.legacy === true, uid: user?.uid, tenantId: runtime?.tenantId };
}
export function SystemPage({ title, access, loading, error, refresh, children }: {
  title: string; access: ReturnType<typeof useSystemAccess>; loading?: boolean; error?: string; refresh?: () => void; children?: ReactNode;
}) {
  return <section className="mx-auto w-full max-w-7xl p-6 text-sm text-ink">
    <header className="mb-4 flex items-center justify-between gap-4 border-b border-line pb-4">
      <div><h1 className="text-[22px] font-semibold">{title}</h1><p className="text-muted">{title === 'Benutzer' ? 'Einstellungen' : 'System · nur lesen'}</p></div>
      {access.allowed && refresh && <button className={systemControl} onClick={refresh} disabled={loading}>Aktualisieren</button>}
    </header>
    {!access.enabled ? <p role="status">{title} nicht verbunden.</p> : !access.allowed ? <p role="status">Nur Inhaber haben Zugriff auf {title}.</p> : <>
      {error && <p role="alert" className="mb-4 border border-line p-3 text-crit">{error}</p>}
      {loading && <div role="status" aria-label={`${title} wird geladen`} className="mb-4 space-y-2"><div className="h-10 bg-line-2" /><div className="h-10 bg-line-2" /></div>}
      {children}
    </>}
  </section>;
}

/** Keep protected pages unmounted while showing named notices instead of error pages. */
export function SystemRoute({ path, title, children }: { path: string; title: string; children: ReactNode }) {
  const access = useSystemAccess(path);
  return access.allowed ? <>{children}</> : <SystemPage title={title} access={access} />;
}
